/**
 * Workbench Header — title, status, and the "primary network actions"
 * toolbar.
 *
 * Layout (left → right):
 *   [name ▸ status]   ·   [presence]   [Save snapshot?]   [Release?]   [⋮]
 *
 * `Save snapshot` and `Release …` are hoisted from the old
 * green banner / Snapshots-tab toolbar so the user can act without
 * scrolling the tab panel or context-switching to the banner. Both
 * appear only when qualified (``canSaveSnapshot`` / ``canRelease``) —
 * the parent (``NetworkWorkbench``) owns that decision.
 *
 * The kebab (⋮) renders its menu through ``PortalMenu`` so it's not
 * clipped by the workbench container's ``overflow: hidden``. See
 * ``.cursor/rules/frontend-dropdown-portal.mdc`` — this file was a
 * listed "known candidate" for conversion until now.
 */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { motion } from 'framer-motion';
import { Save } from 'lucide-react';
import PortalMenu from '@stepflow/ui-shared/components/PortalMenu.jsx';
import NetworkAvatar from '@stepflow/ui-shared/components/dashboard/NetworkAvatar.jsx';
import { formatRelativeTime } from '@stepflow/ui-shared/utils/formatDate';
import { displayVersionLabel } from '@stepflow/ui-shared/utils/stepflowVersionLabel';
import PresenceAvatars from './PresenceAvatars';
import NetworkContextMenuItem from './NetworkContextMenuItem.jsx';

/** Transient / error operational states only. The "Released" /
 *  "In Progress" axis is owned by ``TierBadge`` (keyed on whether a
 *  Version has been cut), so we deliberately don't re-render those
 *  here — that's what was producing two "Released" chips on the same
 *  network. ACTIVE / RELEASED / DRAFT collapse to "no badge"; the
 *  ``TierBadge`` carries the meaningful signal in those steady states.
 */
const StatusBadge = ({ status }) => {
  const s = String(status || '').toUpperCase();
  if (s === 'DEPLOYING' || s === 'BUILDING') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium leading-none bg-yellow-100 text-yellow-700 border-yellow-200">
        <span className="text-[10px] leading-none">⏳</span>
        Building
      </span>
    );
  }
  if (s === 'FAILED') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium leading-none bg-red-100 text-red-700 border-red-200">
        <span className="text-[10px] leading-none">✕</span>
        Failed
      </span>
    );
  }
  return null;
};

/** THE NETWORK TYPE + version chip(s) (owner 2026-10-21). The dormant "○ In Progress" chip is now the network's
 *  TYPE, in the same pill: ``○ Network`` (standard) | ``◆ Expert network`` — the explicit choice made with the Build
 *  deck's Expert toggle. A released network still adds ``● Released: <version>`` beside it. */
const TierBadge = ({ releaseVersion, releases, networkKind = 'standard' }) => {
  const v = Number(releaseVersion);
  const hasVersion = Number.isFinite(v) && v > 0;
  // MAJOR.MINOR label (e.g. "1.10"). Authoritative when the parent passed a row with ``stepflowVersion``; else
  // client-computed from the releases list. Never the retired ``vN`` form.
  const versionLabel = hasVersion ? displayVersionLabel(v, releases) : '';
  const expert = networkKind === 'expert';
  const typeChip = (
    <span
      key="type"
      data-testid="network-type-chip"
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium leading-none ${
        expert ? 'bg-violet-50 text-violet-700 border-violet-200' : 'bg-slate-100 text-slate-600 border-slate-200'}`}
      title={expert
        ? 'Expert network: its Teams, Responsibilities and Rules are the base every room and plugged-in network inherits and adjusts. Change it with the Expert toggle on the Build prompt.'
        : 'Network: a standard network. Turn on Expert on the Build prompt to make it an expert network.'}
    >
      <span className="text-[9px] leading-none">{expert ? '◆' : '○'}</span>
      {expert ? 'Expert network' : 'Network'}
    </span>
  );
  if (!hasVersion) return typeChip;
  return (
    <span className="inline-flex items-center gap-1.5">
      {typeChip}
      <span
        key="released"
        className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium leading-none bg-emerald-50 text-emerald-700 border-emerald-200"
        title={`Released: latest Version is ${versionLabel}. Rooms stay pinned to whichever Version they were created on.`}
      >
        <span className="text-[9px] leading-none">●</span>
        Released: {versionLabel}
      </span>
    </span>
  );
};

export default function WorkbenchHeader({
  network,
  // COMPACT CHROME (ROOMS.md §2.1): rendered INSIDE the shell's one-row dock — no frame, small avatar + name, no
  // id/description/snapshot label; every control kept.
  compact = false,
  onUpdateNetwork,
  onDelete,
  onRelease,
  onSaveSnapshot,
  canRename = true,
  canDelete = true,
  canRelease = false,
  canSaveSnapshot = false,
  // Optional: tailor the Release button label per banner mode
  // (``Release to Agentic Networks`` vs ``Release new version``).
  releaseCtaLabel = 'Create a Room',
  // Level-B state affordance — when the network has at least one user
  // snapshot we render "Last snapshot · 12m ago" next to the Save
  // button. ``null`` (clean first-run) hides the label entirely.
  lastUserSnapshotAt = null,
  lastUserSnapshotLabel = null,
  presence = null,
  currentUserSub = '',
  workbenchConnected = false,
  // Optional: list of NetworkRelease rows used to render the
  // ``MAJOR.MINOR`` label (e.g. "1.10") on the Released chip. When
  // omitted, the chip falls back to legacy ``vN``.
  releases = null,
  // EXPERT-ROOMS §15 — an EXPERT network's "Releases & stages" (cut · promote dev → qa → prod) one click from the menu
  onOpenReleases = null,
}) {
  const [isEditingName, setIsEditingName] = useState(false);
  const [name, setName] = useState(network.name);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuButtonRef = useRef(null);
  useEffect(() => { setName(network.name); }, [network.name]);

  // Tick every 60s so the relative-time label stays fresh without a
  // refetch. We pause while the tab is hidden (parity with the
  // visibility-aware presence heartbeat in useWorkbenchChannel) and
  // resync on return, so background tabs generate zero timer work.
  const [nowTick, setNowTick] = useState(() => Date.now());
  useEffect(() => {
    if (!lastUserSnapshotAt) return undefined;
    if (typeof document === 'undefined') return undefined;
    const tick = () => {
      if (document.visibilityState !== 'hidden') setNowTick(Date.now());
    };
    const id = setInterval(tick, 60_000);
    document.addEventListener('visibilitychange', tick);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [lastUserSnapshotAt]);

  const lastSnapshotRelative = useMemo(() => {
    if (!lastUserSnapshotAt) return null;
    const out = formatRelativeTime(lastUserSnapshotAt);
    // ``formatRelativeTime`` returns '—' for falsy input, but it also
    // returns a locale string for stamps older than 7 days; either is
    // fine to render verbatim. We only hide on literal falsy.
    return out;
    // ``nowTick`` is intentionally a dep so the memo re-computes each
    // minute as time passes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastUserSnapshotAt, nowTick]);

  const lastSnapshotTitle = useMemo(() => {
    if (!lastUserSnapshotAt) return undefined;
    const date = new Date(lastUserSnapshotAt);
    const humanTime = Number.isFinite(date.getTime())
      ? date.toLocaleString()
      : lastUserSnapshotAt;
    return lastUserSnapshotLabel
      ? `"${lastUserSnapshotLabel}" · ${humanTime}`
      : humanTime;
  }, [lastUserSnapshotAt, lastUserSnapshotLabel]);

  const handleSaveName = () => {
    if (name && name !== network.name) {
      onUpdateNetwork({ name });
    }
    setIsEditingName(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSaveName();
    } else if (e.key === 'Escape') {
      setName(network.name);
      setIsEditingName(false);
    }
  };

  // The kebab also hosts the (self-gating) "Network context" item, so show the
  // menu for any real network even when the other actions are unavailable — the
  // item renders nothing if the network isn't released-with-rooms.
  const hasRealNetwork =
    network?.networkId && String(network.networkId).trim() !== '' && network.networkId !== 'new';
  const hasKebab = canRename || canDelete || canRelease || canSaveSnapshot || hasRealNetwork || !!onOpenReleases;

  const showNetworkIdentifier =
    network?.networkId && String(network.networkId).trim() !== '' && network.networkId !== 'new';

  return (
    <div className={compact ? 'flex min-w-0 items-center' : 'shrink-0 border-b border-gray-200 bg-white px-3 py-2 sm:px-4'}>
      <div className={compact ? 'flex min-w-0 items-center gap-2' : 'flex min-h-[36px] items-center justify-between gap-2'}>
        {/* Left: title row — the network's universal avatar (same identity as the
            dashboard tile), then editable name + stable id, then status */}
        <div className="flex min-w-0 items-center gap-2">
          {hasRealNetwork ? (
            <NetworkAvatar networkId={network.networkId} name={network.name} size={compact ? 18 : 26} />
          ) : null}
          {isEditingName ? (
            <h1 className="flex min-w-0 flex-1 items-baseline gap-2 text-base font-semibold leading-tight text-gray-900">
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onBlur={handleSaveName}
                onKeyDown={handleKeyDown}
                autoFocus
                className="min-w-0 flex-1 border-b border-blue-500 bg-transparent text-base font-semibold leading-tight text-gray-900 outline-none"
              />
              {showNetworkIdentifier && !compact ? (
                <code
                  className="shrink-0 font-mono text-xs font-normal text-gray-500"
                  title={network.networkId}
                >
                  {network.networkId}
                </code>
              ) : null}
            </h1>
          ) : (
            <h1 className={`flex min-w-0 items-baseline gap-2 font-semibold leading-tight text-gray-900 ${compact ? 'max-w-[200px] text-sm' : 'text-base'}`}>
              <span
                onClick={() => canRename && setIsEditingName(true)}
                className={`min-w-0 flex-1 truncate transition-colors ${
                  canRename ? 'cursor-pointer hover:text-blue-600' : 'cursor-default'
                }`}
                title={canRename ? 'Click to edit name' : undefined}
              >
                {network.name}
              </span>
              {showNetworkIdentifier && !compact ? (
                <code
                  className="shrink-0 font-mono text-xs font-normal text-gray-500"
                  title={network.networkId}
                >
                  {network.networkId}
                </code>
              ) : null}
            </h1>
          )}
          <StatusBadge status={network.status} />
          <TierBadge
            releaseVersion={network.releaseVersion}
            releases={releases}
            networkKind={network.networkKind || (network.expertNetwork ? 'expert' : 'standard')}
          />
        </div>

        {/* Right: Presence + Primary CTAs + Kebab */}
        <div className="flex shrink-0 items-center gap-2">
          {presence ? (
            <div className="flex items-center gap-1.5">
              <PresenceAvatars presence={presence} currentUserSub={currentUserSub} />
              <span
                aria-label={workbenchConnected ? 'Live — AppSync subscribed' : 'Live connection offline'}
                title={workbenchConnected ? 'Live — AppSync subscribed' : 'Live connection offline'}
                className={`inline-block h-2 w-2 rounded-full ${
                  workbenchConnected
                    // The custom theme mutes emerald-500 to a dark grey-green
                    // (#3a5c4c) that reads as "off" at this size. Use a bright
                    // stock green via arbitrary value so "connected" is obvious,
                    // with a soft glow ring to set it apart from the grey dot.
                    ? 'bg-[#22c55e] shadow-[0_0_0_2px_rgba(34,197,94,0.25)]'
                    : 'bg-gray-300'
                }`}
              />
            </div>
          ) : null}

          {/* "Last snapshot · 12m ago" — Level-B advisory text. The
              actionable buttons (Save snapshot / Release / Promote)
              live in the kebab menu so the header stays uncluttered;
              this label is the only inline affordance kept up here. */}
          {canSaveSnapshot && lastSnapshotRelative && !compact ? (
            <span
              className="hidden whitespace-nowrap text-[11px] leading-none text-gray-500 sm:inline"
              title={lastSnapshotTitle}
              data-testid="workbench-last-snapshot-label"
            >
              Last snapshot · {lastSnapshotRelative}
            </span>
          ) : null}

          {hasKebab ? (
            <>
              <motion.button
                ref={menuButtonRef}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setMenuOpen((v) => !v)}
                className="relative rounded-md p-1.5 text-gray-600 transition-colors hover:bg-gray-100"
                title="Network actions"
                aria-haspopup="menu"
                aria-expanded={menuOpen}
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                </svg>
              </motion.button>
              <PortalMenu
                anchorRef={menuButtonRef}
                open={menuOpen}
                onClose={() => setMenuOpen(false)}
                width={208}
                className="rounded-lg border border-gray-200 bg-white shadow-lg py-1"
              >
                {canRename ? (
                  <button
                    type="button"
                    onClick={() => {
                      setMenuOpen(false);
                      setIsEditingName(true);
                    }}
                    className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm text-gray-700 hover:bg-gray-100"
                    role="menuitem"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    Rename
                  </button>
                ) : null}
                {canSaveSnapshot ? (
                  <button
                    type="button"
                    onClick={() => {
                      setMenuOpen(false);
                      onSaveSnapshot?.();
                    }}
                    className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm text-gray-700 hover:bg-gray-100"
                    role="menuitem"
                  >
                    <Save className="h-4 w-4" aria-hidden />
                    Snapshot
                  </button>
                ) : null}
                {canRelease ? (
                  <button
                    type="button"
                    onClick={() => {
                      setMenuOpen(false);
                      onRelease?.();
                    }}
                    className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm text-gray-700 hover:bg-gray-100"
                    role="menuitem"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19V5m0 0l-7 7m7-7l7 7" />
                    </svg>
                    {releaseCtaLabel}
                  </button>
                ) : null}
                {onOpenReleases && network?.expertNetwork && network?.expertReleases ? (
                  <button
                    type="button"
                    data-testid="menu-expert-releases"
                    onClick={() => {
                      setMenuOpen(false);
                      onOpenReleases();
                    }}
                    className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm text-gray-700 hover:bg-gray-100"
                    role="menuitem"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h10M4 18h6" />
                    </svg>
                    Releases &amp; stages
                  </button>
                ) : null}
                {/* On-demand network-wide context extraction. Self-gates: renders
                    nothing unless the network is released with rooms. */}
                {hasRealNetwork ? (
                  <NetworkContextMenuItem
                    networkId={network.networkId}
                    onActivate={() => setMenuOpen(false)}
                  />
                ) : null}
                {canDelete ? (
                  <button
                    type="button"
                    onClick={() => {
                      setMenuOpen(false);
                      // ``onDelete`` opens DeleteNetworkDialog, which
                      // gates the destructive call behind an impact
                      // preview + type-the-name confirmation. The old
                      // window.confirm path was removed so a misclick
                      // here can no longer wipe a deployed network.
                      onDelete();
                    }}
                    className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm text-red-600 hover:bg-red-50"
                    role="menuitem"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Delete
                  </button>
                ) : null}
              </PortalMenu>
            </>
          ) : null}
        </div>
      </div>

      {/* Optional: Network Description */}
      {network.description && !compact && (
        <p className="mt-1.5 line-clamp-2 text-xs leading-snug text-gray-500">{network.description}</p>
      )}
    </div>
  );
}
